import {
  AnimationClip,
  Material,
  Object3D,
  PMREMGenerator,
  RepeatWrapping,
  sRGBEncoding,
  Texture,
  TextureLoader,
  UnsignedByteType
} from 'three'
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import globalAssetsManifest from '@/features/experience/configs/global-assets-manifest.json'
import CoinDefinition from '@/features/experience/configs/coin-definition'
import Action from '@/common/action'
import debug from 'debug'
import globalRenderer from '@/shared/misc/global-renderer'
import type { IAssets, LoadingItem } from '@/shared/di/interface/assets'
import TingDefinition from '@/features/experience/configs/ting-definition'
import { ICurrencyConfig } from '@/common/currency'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'

const log = debug('assets')

const ASSETS_BASE_FOLDER = './series'

/*export const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('js/draco/')
dracoLoader.path
dracoLoader.preload()
*/
const textureLoader = new TextureLoader()
const gltfLoader = new GLTFLoader()
//gltfLoader.setDRACOLoader(dracoLoader)

const exrLoader = new EXRLoader()
export class AssetsManager implements IAssets {
  #pmremGenerator: PMREMGenerator = {} as PMREMGenerator
  #textureLibrary: Map<string, Texture> = new Map()
  #modelLibrary: Map<string, Object3D> = new Map()
  #animationsLibrary: Map<string, AnimationClip[]> = new Map()
  #materialLibrary: Map<string, Material> = new Map()
  #definitionsLibrary: Map<string, CoinDefinition[]> = new Map()
  #currencyConfigLibrary: Map<string, ICurrencyConfig> = new Map()
  #loadedTokens: Map<string, boolean> = new Map()
  #optionalLoadingItems: LoadingItem[] = []
  onEnvMapUpdated: Action<Texture> = new Action()
  #namespaceWeakMap: WeakMap<any, string> = new WeakMap()
  #loadPromiseCache: Map<string, Promise<any>> = new Map()

  async init(): Promise<void> {
    this.#pmremGenerator = new PMREMGenerator(globalRenderer.getRenderer())
    this.#pmremGenerator.compileEquirectangularShader()

    const allAssets: LoadingItem[] = [...(globalAssetsManifest as LoadingItem[])]
    await this.loadInitialManifest(allAssets)
    return Promise.resolve()
  }

  private addCoinToManifest(coinManifest: LoadingItem[], coinDefinition: CoinDefinition) {
    if (coinManifest.find(x => x.key === coinDefinition.modelName) === undefined) {
      coinManifest.push({
        key: coinDefinition.modelName,
        url: ASSETS_BASE_FOLDER + '/' + coinDefinition.serie + '/models/' + coinDefinition.modelName
      })
    }

    coinManifest.push({
      key: coinDefinition.normalMap,
      url: ASSETS_BASE_FOLDER + '/' + coinDefinition.serie + '/textures/' + coinDefinition.normalMap
    })

    coinManifest.push({
      key: coinDefinition.diffuseMap,
      url: ASSETS_BASE_FOLDER + '/' + coinDefinition.serie + '/textures/' + coinDefinition.diffuseMap
    })
  }

  private addTingToManifest(manifest: LoadingItem[], coinDefinition: TingDefinition) {
    if (manifest.find(x => x.key === coinDefinition.modelName) === undefined) {
      manifest.push({
        key: coinDefinition.modelName,
        url: ASSETS_BASE_FOLDER + '/' + coinDefinition.serie + '/models/' + coinDefinition.modelName
      })
    }

    /*coinManifest.push({
      key: coinDefinition.normalMap,
      url: ASSETS_BASE_FOLDER + '/' + coinDefinition.serie + '/textures/' + coinDefinition.normalMap
    })

    coinManifest.push({
      key: coinDefinition.diffuseMap,
      url: ASSETS_BASE_FOLDER + '/' + coinDefinition.serie + '/textures/' + coinDefinition.diffuseMap
    })*/
  }

  loadSingleCoin(coinDefinition: CoinDefinition): Promise<void> {
    //TODO: change this when decided on series/bundle etc
    const key = coinDefinition.serie + ':' + Number(coinDefinition.denomination).toString()

    if (this.isTokenLoaded(key)) {
      return Promise.resolve()
    }

    const coinManifest: LoadingItem[] = []
    this.addCoinToManifest(coinManifest, coinDefinition)
    return new Promise<void>((resolve, reject) => {
      this.loadManifest(coinManifest, 'coins').then(
        () => {
          this.#loadedTokens.set(key, true)
          resolve()
        },
        () => {
          reject()
        }
      )
    })
  }

  loadSingleTing(tingDefinition: TingDefinition): Promise<void> {
    //TODO: change this when decided on series/bundle etc
    const key = tingDefinition.serie + ':' + tingDefinition.modelName
    if (this.isTokenLoaded(key)) {
      return Promise.resolve()
    }

    const manifest: LoadingItem[] = []
    this.addTingToManifest(manifest, tingDefinition)
    return new Promise<void>((resolve, reject) => {
      this.loadManifest(manifest, 'tings').then(
        () => {
          this.#loadedTokens.set(key, true)
          resolve()
        },
        () => {
          reject()
        }
      )
    })
  }

  isTokenLoaded(key: string): boolean {
    if (this.#loadedTokens.has(key) && this.#loadedTokens.get(key)) {
      return true
    }
    return false
  }

  //TODO: The promise stored here contains references to all previous promises in the chain because it's connected in a await.
  async loadDefinitions(definitionsUrl: string): Promise<CoinDefinition[]> {
    const urlLowerCase = definitionsUrl.toLowerCase()

    if (this.#definitionsLibrary.has(urlLowerCase)) {
      return Promise.resolve(this.#definitionsLibrary.get(urlLowerCase)!)
    }

    //only load once, so concurrent coin-requests will reuse the same definition data

    if (!this.#loadPromiseCache.has(urlLowerCase)) {
      this.#loadPromiseCache.set(
        urlLowerCase,
        fetch(urlLowerCase, {
          method: 'GET'
        }).then(response => {
          return response.json()
        })
      )
    }

    //wrap in try/catch do remove cached promise if failed
    try {
      const data = await this.#loadPromiseCache.get(urlLowerCase)

      if (data.definitions) {
        this.#definitionsLibrary.set(urlLowerCase, data.definitions)
        return Promise.resolve(data.definitions)
      } else {
        return Promise.reject()
      }
    } catch (err: any) {
      this.#loadPromiseCache.delete(urlLowerCase)
      console.warn('failed load definition file')
      return Promise.reject()
    }
  }

  //TODO: The promise stored here contains references to all previous promises in the chain because it's connected in a await.
  async loadCurrencyUXConfig(configUrl: string): Promise<ICurrencyConfig> {
    const urlLowerCase = configUrl.toLowerCase()

    if (this.#currencyConfigLibrary.has(urlLowerCase)) {
      return Promise.resolve(this.#currencyConfigLibrary.get(urlLowerCase)!)
    }

    //only load once, so concurrent coin-requests will reuse the same definition data

    if (!this.#loadPromiseCache.has(urlLowerCase)) {
      this.#loadPromiseCache.set(
        urlLowerCase,
        fetch(urlLowerCase, {
          method: 'GET'
        }).then(response => {
          return response.json()
        })
      )
    }

    //wrap in try/catch do remove cached promise if failed
    try {
      const data = await this.#loadPromiseCache.get(urlLowerCase)

      if (data) {
        this.#currencyConfigLibrary.set(urlLowerCase, data)
        return Promise.resolve(data)
      } else {
        return Promise.reject()
      }
    } catch (err: any) {
      this.#loadPromiseCache.delete(urlLowerCase)
      console.warn('failed load config file')
      return Promise.reject()
    }
  }

  loadInitialManifest(allAssets: LoadingItem[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.loadManifest(allAssets, 'global').then(
        () => {
          resolve()
        },
        () => {
          reject()
        }
      )
    })
  }

  loadRoomManifest(room: string, manifest: LoadingItem[]): Promise<void> {
    this.#optionalLoadingItems.push(...manifest.filter(item => !item.loadOnInit))
    return this.loadManifest(
      manifest.filter(item => item.loadOnInit),
      room as string
    )
  }

  loadManifest(manifest: LoadingItem[], namespace: string): Promise<void> {
    const allPromises: Promise<void>[] = manifest.map(item => {
      return new Promise<void>((resolve, reject) => {
        log('load asset', item.key)

        const onItemLoaded = () => {
          log('item loaded', item)
          resolve()
        }
        const onItemProgress = () => {}
        const onItemError = () => {
          console.log('error', item)
          reject()
        }

        if (item.onlyPreload) {
          if (item.url.includes('.png') || item.url.includes('.jpg')) {
            const img = new Image()
            img.onload = function () {
              onItemLoaded()
            }
            img.onerror = function () {
              //resolve anyway, only for preloading so does not matter
              onItemLoaded()
            }
            img.src = item.url
          } else {
            onItemLoaded()
          }

          return
        }

        if (item.url.includes('.png') || item.url.includes('.jpg')) {
          textureLoader.load(
            item.url,
            texture => {
              texture.flipY = false
              texture.wrapS = texture.wrapT = RepeatWrapping
              texture.encoding = sRGBEncoding
              this.addTexture(item.key, texture, namespace)
              onItemLoaded()
            },
            onItemProgress,
            onItemError
          )
        } else if (item.url.includes('.gltf') || item.url.includes('.glb')) {
          gltfLoader.load(
            item.url,
            gltf => {
              this.addAnimations(item.key, gltf.animations, namespace)
              this.addModel(item.key, gltf.scene as Object3D, namespace)
              onItemLoaded()
            },
            onItemProgress,
            onItemError
          )
        } else if (item.url.includes('.exr')) {
          exrLoader.setDataType(UnsignedByteType).load(
            item.url,
            (texture: Texture) => {
              texture.flipY = false
              //texture.encoding = sRGBEncoding
              const exrCubeRenderTarget = this.#pmremGenerator.fromEquirectangular(texture)
              this.addTexture(item.key, exrCubeRenderTarget.texture, namespace)

              texture.dispose()
              onItemLoaded()
            },
            onItemProgress,
            onItemError
          )
        }
      })
    })

    return Promise.all(allPromises).then(
      () => {
        log('all items loaded')
      },
      error => {
        console.error('Could not load all textures:', error)
      }
    )
  }

  unloadManifest(manifest: LoadingItem[]): void {
    manifest.forEach((item: LoadingItem) => {
      this.removeTexture(item.key)
      this.removeModel(item.key)
    })
  }

  unloadNamespace(namespace: string): void {
    if (namespace === 'coins') {
      this.#loadedTokens.clear()
    }

    //remove textures
    const removeTextureKeys: string[] = []
    this.#textureLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeTextureKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeTextureKeys.forEach(key => {
      this.removeTexture(key)
    })

    //remove models
    const removeModelKeys: string[] = []
    this.#modelLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeModelKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeModelKeys.forEach(key => {
      this.removeModel(key)
    })

    //remove animations
    const removeAnimationsKeys: string[] = []
    this.#animationsLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeAnimationsKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeAnimationsKeys.forEach(key => {
      this.removeAnimations(key)
    })

    //remove materials
    const removeMaterialsKeys: string[] = []
    this.#materialLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeMaterialsKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeMaterialsKeys.forEach(key => {
      this.removeMaterial(key)
    })
  }

  addTexture(key: string, texture: Texture, namespace: string): void {
    if (this.#textureLibrary.has(key)) {
      log('texture already exists')
      return
    } else {
      this.#namespaceWeakMap.set(texture, namespace)
      this.#textureLibrary.set(key, texture)
    }
  }

  getTexture(key: string): Texture | null {
    if (this.#textureLibrary.has(key)) {
      const texture = this.#textureLibrary.get(key)

      if (texture) {
        return texture
      } else {
        return null
      }
    } else {
      log('texture not available: ' + key)
      return null
    }
  }

  getTextureAsync(key: string, namespace: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      const existingTexture = this.getTexture(key)
      if (existingTexture) {
        resolve(existingTexture)
      } else {
        const optionalAsset = this.#optionalLoadingItems.find(item => item.key === key)
        if (optionalAsset) {
          textureLoader.load(optionalAsset.url, texture => {
            texture.flipY = false
            texture.wrapS = texture.wrapT = RepeatWrapping
            texture.encoding = sRGBEncoding
            this.addTexture(optionalAsset.key, texture, namespace)
            resolve(texture)
          })
        } else {
          console.warn('opn', key, this.#optionalLoadingItems)
          debug('optional asset does not exist: ' + key)
          reject()
        }
      }
    })
  }

  loadTexture(key: string, url: string, namespace: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      const existingTexture = this.getTexture(key)
      if (existingTexture) {
        resolve(existingTexture)
      } else {
        textureLoader.load(url, texture => {
          texture.flipY = false
          texture.wrapS = texture.wrapT = RepeatWrapping
          texture.encoding = sRGBEncoding
          this.addTexture(key, texture, namespace)
          resolve(texture)
        })
      }
    })
  }

  removeTexture(key: string): void {
    if (this.#textureLibrary.has(key)) {
      //TODO: unload/dispose
      this.#textureLibrary.get(key)?.dispose()
      this.#textureLibrary.delete(key)
    }
  }

  addModel(key: string, model: Object3D, namespace: string): void {
    model.userData.enableBloom = false
    //model.userData.hideFromBloom = true
    if (this.#modelLibrary.has(key)) {
      log('mesh already exists' + key)
    } else {
      this.#namespaceWeakMap.set(model, namespace)
      this.#modelLibrary.set(key, model)
    }
  }

  getModel(key: string): Object3D | undefined {
    if (this.#modelLibrary.has(key)) {
      return this.#modelLibrary.get(key)
    } else {
      log('mesh not available: ' + key)
      return
    }
  }

  removeModel(key: string): void {
    if (this.#modelLibrary.has(key)) {
      //TODO: unload/dispose
      this.#modelLibrary.delete(key)
    }
  }

  addAnimations(key: string, animations: AnimationClip[], namespace: string): void {
    if (this.#animationsLibrary.has(key)) {
      log('animations already exists' + key)
    } else {
      this.#animationsLibrary.set(key, animations)
      this.#namespaceWeakMap.set(animations, namespace)
    }
  }

  getAnimations(key: string): AnimationClip[] | undefined {
    if (this.#animationsLibrary.has(key)) {
      return this.#animationsLibrary.get(key)
    } else {
      log('mesh not available: ' + key)
      return
    }
  }

  removeAnimations(key: string): void {
    if (this.#animationsLibrary.has(key)) {
      //TODO: unload/dispose
      this.#animationsLibrary.delete(key)
    }
  }

  addMaterial(key: string, material: Material, namespace: string): void {
    if (this.#materialLibrary.has(key)) {
      log('animations already exists' + key)
    }
    this.#namespaceWeakMap.set(material, namespace)
    this.#materialLibrary.set(key, material)
  }

  getMaterial(key: string): Material | undefined {
    if (this.#materialLibrary.has(key)) {
      return this.#materialLibrary.get(key)
    } else {
      log('mesh not available: ' + key)
      return
    }
  }

  removeMaterial(key: string): void {
    if (this.#materialLibrary.has(key)) {
      //TODO: unload/dispose
      this.#materialLibrary.delete(key)
    }
  }

  /*getDracoLoader(): DRACOLoader {
    return dracoLoader
  }*/

  getTextureListInNamespace(namespace: string): string[] {
    const list: string[] = []
    this.#textureLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        list.push(key)
      }
    })

    return list
  }
}
